import React, { useEffect, useState } from 'react';
import Modal from '../generic/Modal';
import SyntaxHighlighter from '../generic/SyntaxHighlighter';
import Table from '../Table';
import { formatJSONContent, isNullOrEmpty } from '../../utils/core';
import Truncate from '../Truncate';
import { pathOr } from 'ramda';

/**
 * @param {String} text Text content that will be displayed in the modal
 * @description Tries (very roughly) to figure out what syntax highlighting to use for given text.
 */
export const syntax = (text) => {
    const isHtmlLike = str => /<[a-z][\s\S]*>/i.test(str);
    const isJsonLike = str => /\{(.*?)\}|\[(.*?)\]/g.test(str);

    if (!isNullOrEmpty(text)) {
        return isHtmlLike(text)
            ? 'html'
            : isJsonLike(text)
                ? 'json'
                : 'text';
    }
    return 'text';
};

/**
 * @param {String} text Text that should be displayed in the table cell and modal
 * @param {Number} limit Limit the number of characters shown in the table cell
 * @param {String} title Title of the modal
 * @param {Function} setContent Set the content into the component state
 * @description Shows the data in the table cell. If data was truncated then it also gives the option to view the complete data in a modal.
 */
export const truncateText = (text, limit, title, setContent) => {
    const viewButton = text.length >= limit
        ? (
            <button
                className="link-emulate"
                onClick={() => setContent({ text, title })}
            >
                View
            </button>
        )
        : null;

    return (
        <>
            <Truncate text={text} maxLength={limit} />
            &nbsp;
            {viewButton}
        </>
    );
};


/**
 * @param {Object} selectedState Holds the data related to the selected state
 * @description This component renders a table with Root Faults related to a state (if any are found in the state data)
 */
const RootFaultsList = ({ selectedState }) => {
    const [faults, setFaults] = useState([]);
    const [content, setContent] = useState(null);

    useEffect(() => {
        if (selectedState.hasRootFaults) {
            const remappedFaults = Object.entries(selectedState.rootFaults)
                .map(fault => ({
                    name: fault[0],
                    data: JSON.parse(selectedState.rootFaults[fault[0]]),
                }));

            setFaults(remappedFaults);
        }
    }, [selectedState]);

    const columns = [
        {
            renderHeader: () => 'Name',
            renderCell: ({ item }) => pathOr('', ['name'], item),
            size: '200px',
        },
        {
            renderHeader: () => 'URI',
            renderCell: ({ item }) => pathOr('', ['data', 'name'], item),
        },
        {
            renderHeader: () => 'Message',
            renderCell: ({ item }) => truncateText(pathOr('', ['data', 'message'], item), 65, 'Message', setContent),
            size: '250px',
        },
        {
            renderHeader: () => 'Status Code',
            renderCell: ({ item }) => pathOr('', ['data', 'statusCode'], item),
            size: '100px',
        },
        {
            renderHeader: () => 'Response Body',
            renderCell: ({ item }) => truncateText(pathOr('', ['data', 'responseBody'], item), 65, 'Response Body', setContent),
        },
        {
            renderHeader: () => 'Response Headers',
            renderCell: ({ item }) => pathOr(null, ['data', 'responseHeaders'], item) && (
                <button
                    className="link-emulate"
                    onClick={() => setContent({ text: JSON.stringify(item.data.responseHeaders), title: 'Response Headers' })}
                >
                    View
                </button>
            ),
            size: '200px',
        },
    ];

    return (
        <div>
            <Table
                className="table"
                columns={columns}
                items={faults}
            />

            <Modal
                show={!isNullOrEmpty(content)}
                onHide={() => setContent(null)}
                title={pathOr('', ['title'], content)}
                renderBody={() => (
                    <SyntaxHighlighter
                        content={formatJSONContent(pathOr('', ['text'], content))}
                        language={syntax(pathOr('', ['text'], content))}
                    />
                )}
                renderFooter={() => (
                    <div className="flex">
                        <div className="flex-child-right">
                            <button className="btn btn-sm btn-default" onClick={() => setContent(null)}>
                                Close
                            </button>
                        </div>
                    </div>
                )}
            />
        </div>
    );
};

export default RootFaultsList;
